import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  GeolocationState,
  GeolocationStatus,
  useGeolocation,
} from '@/hooks/useGeolocation';
import { useUiConfig } from '@/services/config';
import { useApiClient } from '@/services/useApiClient';
import { useBackgroundLocationSync } from '@/services/backgroundLocationSync';

const LOCATION_REQUEST_STORAGE_KEY = 'awkherd.location.requested';
const LOCATION_COORDS_STORAGE_KEY = 'awkherd.location.coords';

const loadRequestedFlag = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.localStorage.getItem(LOCATION_REQUEST_STORAGE_KEY) === 'true';
};

const loadCachedCoords = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const raw = window.localStorage.getItem(LOCATION_COORDS_STORAGE_KEY);
  if (!raw) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as GeolocationState['coords'];
    if (
      parsed &&
      typeof parsed.latitude === 'number' &&
      typeof parsed.longitude === 'number'
    ) {
      return parsed;
    }
  } catch (error) {
    console.warn('Unable to parse cached location', error);
  }
  return undefined;
};

const GEO_PERMISSION_DENIED = 1;
const GEO_POSITION_UNAVAILABLE = 2;
const GEO_TIMEOUT = 3;

type IpLocationResponse = {
  timestamp?: string;
  ip?: string;
  location?: {
    latitude?: number;
    longitude?: number;
    accuracy?: number;
  } | null;
};

type LocationContextValue = GeolocationState & {
  hasRequested: boolean;
  requestPermission: () => void;
  forceLocationUpdate?: () => Promise<void>;
  isUpdatingProfile?: boolean;
};

const fallbackState: LocationContextValue = {
  status: typeof window === 'undefined' ? 'unsupported' : 'idle',
  hasRequested: false,
  requestPermission: () => undefined,
};

const LocationContext = createContext<LocationContextValue>(fallbackState);

const shouldDisableWatch = (status: GeolocationStatus) =>
  status === 'denied' || status === 'unsupported';

export const LocationProvider = ({ children }: { children: ReactNode }) => {
  const { data: uiConfig } = useUiConfig();
  const api = useApiClient();

  // Enable background location sync to automatically update user profile
  const locationSync = useBackgroundLocationSync();

  const initialRequested = loadRequestedFlag();
  const initialCoords = loadCachedCoords();

  const [enabled, setEnabled] = useState<boolean>(initialRequested);
  const [hasRequested, setHasRequested] = useState<boolean>(initialRequested);
  const [cachedCoords, setCachedCoords] =
    useState<GeolocationState['coords']>(initialCoords);
  const [accuracyMode, setAccuracyMode] = useState<'high' | 'low'>('high');
  const [ipFallback, setIpFallback] = useState<{
    status: 'idle' | 'loading' | 'success' | 'error';
    coords?: GeolocationState['coords'];
    error?: unknown;
  }>({ status: 'idle' });
  const ipRequestRef = useRef(false);
  const [lowAccuracyAttempt, setLowAccuracyAttempt] = useState<
    'idle' | 'pending' | 'finished'
  >('idle');
  const previousAccuracyRef = useRef<'high' | 'low'>('high');
  const [lowAttemptStarted, setLowAttemptStarted] = useState(false);

  const baseTimeout = useMemo(
    () => uiConfig?.location?.timeout ?? 20_000,
    [uiConfig?.location?.timeout],
  );
  const baseMaxAge = useMemo(
    () => uiConfig?.location?.maxAge ?? 180_000,
    [uiConfig?.location?.maxAge],
  );
  const highAccuracyEnabled = useMemo(
    () => uiConfig?.location?.highAccuracy ?? true,
    [uiConfig?.location?.highAccuracy],
  );
  const usingHighAccuracy = accuracyMode === 'high' && highAccuracyEnabled;

  const geolocation = useGeolocation({
    enabled,
    highAccuracy: usingHighAccuracy,
    timeout: usingHighAccuracy ? baseTimeout : Math.max(baseTimeout, 10_000),
    maximumAge: usingHighAccuracy ? baseMaxAge : Math.max(baseMaxAge, 300_000),
  });
  const ipEndpoint = uiConfig?.api?.endpoints?.ipLocation ?? '/ip-location';
  const ipFeatureEnabled = uiConfig?.features?.ipLocation ?? true;

  useEffect(() => {
    if (uiConfig?.location?.highAccuracy === false && accuracyMode !== 'low') {
      setAccuracyMode('low');
    }
  }, [accuracyMode, uiConfig?.location?.highAccuracy]);

  // Only log geolocation settings when accuracy mode changes (not on every config update)
  useEffect(() => {
    if (!enabled) {
      return;
    }
    console.info('[LocationProvider] Using geolocation with', {
      mode: accuracyMode,
      highAccuracyRequested: usingHighAccuracy,
      timeout: usingHighAccuracy ? baseTimeout : Math.max(baseTimeout, 10_000),
      maximumAge: usingHighAccuracy ? baseMaxAge : Math.max(baseMaxAge, 300_000),
    });
  }, [accuracyMode, enabled, usingHighAccuracy]);

  useEffect(() => {
    if (previousAccuracyRef.current === accuracyMode) {
      return;
    }
    if (accuracyMode === 'low') {
      setLowAccuracyAttempt('pending');
      setLowAttemptStarted(false);
    } else {
      setLowAccuracyAttempt('idle');
      setLowAttemptStarted(false);
    }
    previousAccuracyRef.current = accuracyMode;
  }, [accuracyMode]);

  const requestIpFallback = useCallback(async () => {
    if (!ipFeatureEnabled) {
      console.warn('[LocationProvider] IP fallback disabled via feature flag.');
      return;
    }
    if (ipRequestRef.current) {
      return;
    }
    ipRequestRef.current = true;
    setIpFallback({ status: 'loading' });
    try {
      const response = await api.get<IpLocationResponse>(ipEndpoint);
      const latitude = response?.location?.latitude;
      const longitude = response?.location?.longitude;
      if (typeof latitude !== 'number' || typeof longitude !== 'number') {
        throw new Error('Invalid IP location response payload.');
      }
      const coords: GeolocationState['coords'] = {
        latitude,
        longitude,
        accuracy:
          typeof response?.location?.accuracy === 'number'
            ? response.location.accuracy
            : undefined,
        timestamp: Date.now(),
      };
      setLowAccuracyAttempt('finished');
      setHasRequested(true);
      setIpFallback({ status: 'success', coords });
      setCachedCoords(coords);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(LOCATION_COORDS_STORAGE_KEY, JSON.stringify(coords));
      }
      console.info('[LocationProvider] Falling back to IP-based coordinates', {
        ip: response?.ip,
        coords,
      });
    } catch (error) {
      const details =
        error instanceof Error
          ? { message: error.message, stack: error.stack }
          : { error };
      console.error('[LocationProvider] IP fallback lookup failed', details);
      setLowAccuracyAttempt('finished');
      setIpFallback({ status: 'error', error });
    } finally {
      ipRequestRef.current = false;
    }
  }, [api, ipEndpoint, ipFeatureEnabled]);

  useEffect(() => {
    if (shouldDisableWatch(geolocation.status)) {
      setEnabled(false);
    }
    if (geolocation.status === 'granted') {
      setHasRequested(true);
      setLowAccuracyAttempt('finished');
      setIpFallback((prev) => (prev.status !== 'idle' ? { status: 'idle' } : prev));
    }
    if (geolocation.status === 'error') {
      console.error('[LocationProvider] Geolocation reported error status', {
        error: geolocation.error,
        code: geolocation.errorCode,
      });
    }
  }, [geolocation.error, geolocation.errorCode, geolocation.status]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (geolocation.status !== 'error') {
      return;
    }
    if (geolocation.errorCode === GEO_PERMISSION_DENIED) {
      return;
    }
    if (accuracyMode === 'high') {
      console.warn(
        '[LocationProvider] High accuracy geolocation failed. Retrying without high accuracy.',
        {
          error: geolocation.error,
          code: geolocation.errorCode,
        },
      );
      setAccuracyMode('low');
      return;
    }
    if (accuracyMode === 'low' && lowAccuracyAttempt === 'pending') {
      console.warn(
        '[LocationProvider] Low accuracy geolocation failed. Falling back to IP lookup.',
        {
          error: geolocation.error,
          code: geolocation.errorCode,
        },
      );
      setLowAccuracyAttempt('finished');
      requestIpFallback();
    }
  }, [
    accuracyMode,
    enabled,
    geolocation.error,
    geolocation.errorCode,
    geolocation.status,
    lowAccuracyAttempt,
    requestIpFallback,
  ]);

  useEffect(() => {
    if (geolocation.status === 'granted' && geolocation.coords) {
      setCachedCoords(geolocation.coords);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(
          LOCATION_COORDS_STORAGE_KEY,
          JSON.stringify(geolocation.coords),
        );
      }
      console.debug('[LocationProvider] Cached coordinates updated', geolocation.coords);
    }

    if (geolocation.status === 'denied' || geolocation.status === 'unsupported') {
      setCachedCoords(undefined);
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(LOCATION_COORDS_STORAGE_KEY);
      }
      console.warn('[LocationProvider] Location unavailable', {
        status: geolocation.status,
        error: geolocation.error,
      });
    }
  }, [geolocation.status, geolocation.coords]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (hasRequested) {
      window.localStorage.setItem(LOCATION_REQUEST_STORAGE_KEY, 'true');
    } else {
      window.localStorage.removeItem(LOCATION_REQUEST_STORAGE_KEY);
    }
  }, [hasRequested]);

  useEffect(() => {
    if (typeof navigator === 'undefined') {
      if (hasRequested) {
        setEnabled(true);
      }
      return;
    }

    const permissions = (
      navigator as Navigator & {
        permissions?: Permissions;
      }
    ).permissions;

    if (!permissions || !permissions.query) {
      if (hasRequested) {
        setEnabled(true);
      }
      return;
    }

    let cancelled = false;
    let cleanup: (() => void) | null = null;

    const applyPermissionState = (state: PermissionState) => {
      if (cancelled) {
        return;
      }
      if (state === 'granted') {
        setHasRequested(true);
        setEnabled(true);
      } else if (state === 'prompt') {
        if (hasRequested) {
          setEnabled(true);
        }
      } else if (state === 'denied') {
        setHasRequested(true);
        setEnabled(false);
      }
    };

    permissions
      .query({ name: 'geolocation' as PermissionName })
      .then((status) => {
        if (cancelled) {
          return;
        }
        applyPermissionState(status.state);
        const handleChange = () => applyPermissionState(status.state);
        if (typeof status.addEventListener === 'function') {
          status.addEventListener('change', handleChange);
          cleanup = () => status.removeEventListener('change', handleChange);
        } else {
          status.onchange = handleChange;
          cleanup = () => {
            if (status.onchange === handleChange) {
              status.onchange = null;
            }
          };
        }
        console.debug('[LocationProvider] Permission query resolved', {
          state: status.state,
        });
      })
      .catch(() => {
        console.warn(
          '[LocationProvider] Permission query failed, falling back to manual enable',
        );
        if (hasRequested) {
          setEnabled(true);
        }
      });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [hasRequested]);

  const requestPermission = useCallback(() => {
    const prefersHigh = uiConfig?.location?.highAccuracy ?? true;
    const nextAccuracy = prefersHigh ? 'high' : 'low';
    setHasRequested(true);
    setEnabled(true);
    setAccuracyMode(nextAccuracy);
    setLowAccuracyAttempt(nextAccuracy === 'low' ? 'pending' : 'idle');
    setIpFallback({ status: 'idle' });
    previousAccuracyRef.current = nextAccuracy;
  }, [uiConfig?.location?.highAccuracy]);

  const effectiveCoords =
    geolocation.coords ?? ipFallback.coords ?? cachedCoords ?? undefined;

  const statusForConsumers: GeolocationStatus = (() => {
    if (geolocation.status === 'denied' || geolocation.status === 'unsupported') {
      return geolocation.status;
    }
    if (effectiveCoords) {
      return 'granted';
    }
    return geolocation.status;
  })();

  const value = useMemo<LocationContextValue>(
    () => ({
      status: statusForConsumers,
      coords: effectiveCoords,
      error: geolocation.error,
      hasRequested,
      requestPermission,
      forceLocationUpdate: locationSync.forceLocationUpdate,
      isUpdatingProfile: locationSync.isUpdating,
    }),
    [
      effectiveCoords,
      geolocation.error,
      hasRequested,
      requestPermission,
      statusForConsumers,
      locationSync.forceLocationUpdate,
      locationSync.isUpdating,
    ],
  );

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
};

export const useLocation = () => useContext(LocationContext);
